import { animate, motion, useInView, useReducedMotion } from "motion/react";
import { useEffect, useRef, useState, type ReactNode } from "react";

/** A long, soft settle (cubic-bezier(.22,1,.36,1)): fast out of the start, slow into place. */
export const ease = [0.22, 1, 0.36, 1] as const;

const FROM = {
  up: { opacity: 0, y: 18 },
  left: { opacity: 0, x: -14 },
  right: { opacity: 0, x: 14 },
  scale: { opacity: 0, scale: 0.97, y: 10 },
  tilt: { opacity: 0, y: 24, rotate: -1.2 },
} as const;

/**
 * Rises (or slides) into place the first time it scrolls into view. Under
 * "reduce motion" (MotionConfig in main.tsx) it simply appears.
 */
export function Reveal({ children, delay = 0, from = "up", className, amount = 0.25, duration = 0.9 }: { children: ReactNode; delay?: number; from?: keyof typeof FROM; className?: string; amount?: number; duration?: number }) {
  return (
    <motion.div initial={FROM[from]} whileInView={{ opacity: 1, x: 0, y: 0, scale: 1, rotate: 0 }} viewport={{ once: true, amount }} transition={{ duration, delay, ease }} className={className}>
      {children}
    </motion.div>
  );
}

/**
 * A picture uncovered like a brush stroke across paper, from the left or the
 * right. The outer box watches the viewport: a fully clipped element never
 * counts as "in view", so the clipping sits on the layer inside it.
 */
export function InkReveal({ children, from = "left", className, delay = 0 }: { children: ReactNode; from?: "left" | "right"; className?: string; delay?: number }) {
  const hidden = from === "left" ? "inset(0% 100% 0% 0%)" : "inset(0% 0% 0% 100%)";
  return (
    <motion.div initial="hidden" whileInView="shown" viewport={{ once: true, amount: 0.3 }} className={className}>
      <motion.div className="absolute inset-0" variants={{ hidden: { clipPath: hidden }, shown: { clipPath: "inset(0% 0% 0% 0%)", transition: { duration: 1.1, delay, ease: [0.65, 0, 0.35, 1] } } }}>
        <motion.div className="absolute inset-0" variants={{ hidden: { scale: 1.12 }, shown: { scale: 1, transition: { duration: 1.6, delay, ease } } }}>
          {children}
        </motion.div>
      </motion.div>
    </motion.div>
  );
}

/** A number counted up from zero when it comes into view. */
export function CountUp({ value, duration = 1.1 }: { value: number; duration?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const seen = useInView(ref, { once: true });
  const reduce = useReducedMotion();
  const [shown, setShown] = useState(reduce ? value : 0);
  useEffect(() => {
    if (!seen || reduce) return setShown(value);
    const controls = animate(0, value, { duration, ease: "easeOut", onUpdate: (v) => setShown(Math.round(v)) });
    return () => controls.stop();
  }, [seen, reduce, value, duration]);
  return <span ref={ref}>{shown.toLocaleString()}</span>;
}
